import React, { useState, useRef, useEffect } from "react";
import InforBtn from "@/assets/info-circle.svg";

interface TooltipProps {
  text: string;
  zIndex?: number;
}

const Tooltip: React.FC<TooltipProps> = ({ text, zIndex = 103 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<
    "left" | "right" | "center"
  >("left");
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isVisible || !tooltipRef.current || !triggerRef.current) return;

    const calculatePosition = () => {
      const trigger = triggerRef.current!;
      const tooltip = tooltipRef.current!;
      const triggerRect = trigger.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      tooltip.style.visibility = "hidden";
      tooltip.style.opacity = "1";
      const tooltipRect = tooltip.getBoundingClientRect();
      tooltip.style.visibility = "";
      tooltip.style.opacity = "";

      const spaceOnLeft = triggerRect.left;
      const spaceOnRight = viewportWidth - triggerRect.right;
      const tooltipWidth = tooltipRect.width;

      if (spaceOnRight >= tooltipWidth + 16) {
        setTooltipPosition("left");
      } else if (spaceOnLeft >= tooltipWidth + 16) {
        setTooltipPosition("right");
      } else {
        setTooltipPosition("center");
      }
    };

    const timeoutId = setTimeout(calculatePosition, 10);
    return () => clearTimeout(timeoutId);
  }, [isVisible, text]);

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const getTooltipClasses = () => {
    const baseClasses = `absolute top-full mt-2 bg-black text-white text-[14px] px-3 py-2 rounded shadow-lg transition-opacity duration-300 whitespace-normal pointer-events-none w-max max-w-[280px] sm:max-w-[320px]`;

    const zIndexClass = zIndex === 106 ? "z-[106]" : "z-[103]";

    switch (tooltipPosition) {
      case "left":
        return `${baseClasses} ${zIndexClass} left-0`;
      case "right":
        return `${baseClasses} ${zIndexClass} right-0`;
      case "center":
        return `${baseClasses} ${zIndexClass} left-1/2 -translate-x-1/2`;
      default:
        return `${baseClasses} ${zIndexClass} left-0`;
    }
  };

  const getArrowClasses = () => {
    const baseArrowClasses =
      "absolute top-0 -translate-y-1/2 h-2 w-2 rotate-45 bg-black";

    switch (tooltipPosition) {
      case "left":
        return `${baseArrowClasses} left-2`;
      case "right":
        return `${baseArrowClasses} right-2`;
      case "center":
        return `${baseArrowClasses} left-1/2 -translate-x-1/2`;
      default:
        return `${baseArrowClasses} left-2`;
    }
  };

  return (
    <div
      className="relative inline-block"
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <img
        src={InforBtn}
        alt="Information"
        className="inline-block hover:opacity-80 transition-opacity cursor-pointer"
      />
      <div
        ref={tooltipRef}
        className={`${getTooltipClasses()} ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
      >
        {text}
        <span className={getArrowClasses()}></span>
      </div>
    </div>
  );
};

export default Tooltip;
